import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { postQuery } from "../../../services/BackendServices";
import config from "../../../aws-exports";

const GeneralSettings = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  // Helper function to construct S3 URL for product images
  const getProductImageUrl = (imagePath) => {
    if (!imagePath) return "https://grains.global/assets/images/white-rice.png";

    const bucket = process.env.REACT_APP_AMTPL_BUCKET;
    const region = config.aws_project_region;

    // If imagePath already contains full URL, return as is
    if (imagePath.startsWith("http")) return imagePath;

    // Construct S3 URL
    return `https://${bucket}.s3.${region}.amazonaws.com/public/images/${imagePath}`;
  };

  // Helper function to format date to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return null;
    }
  };

  const handleSyncWebsite = async () => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      // Step 1: Fetch offers from database
      console.log("Fetching offers from database...");
      const offersResponse = await postQuery({
        target: "syncWebsiteData",
        type: "offer",
      });

      // Step 2: Fetch products from database
      console.log("Fetching products from database...");
      const productsResponse = await postQuery({
        target: "syncWebsiteData",
        type: "product",
      });

      // Step 3: Transform and send offers to external website
      console.log(
        "Syncing offers to external website...",
        offersResponse,
        productsResponse
      );
      const offersData = offersResponse.items.data.map((offer) => ({
        offer_id: offer.GPOfferExtendedID || offer.GPOfferID,
        offer_number: offer.OfferNumber,
        product_id: offer.ProductID,
        quantity: parseFloat(offer.Quantity) || 0,
        price: parseFloat(offer.FOBSalePrice) || 0,
        origin: offer.OriginCountry || offer.Country,
        destination: offer.DestinationCountry,
        incoterms: offer.INCO || "FOB",
        expiry_date: formatDate(offer.ExpiryDate),
      }));

      const offersExternalResponse = await fetch(
        "https://grains.global/post_marketplace_offers.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer AMTPL2025smTKWbApp",
          },
          body: JSON.stringify(offersData),
        }
      );

      if (!offersExternalResponse.ok) {
        throw new Error(
          `Failed to sync offers to external website: ${offersExternalResponse.statusText}`
        );
      }

      // Step 4: Transform and send products to external website
      console.log("Syncing products to external website...");
      const productsData = productsResponse.items.data.map((product) => ({
        product_id: product.ProductID,
        name: product.ProductName,
        grade: product.Grade || "",
        hscode: product.HsCode || "",
        variety: product.Variety || "",
        originCountry: product.OriginCountry || "India",
        avgPriceMT: parseFloat(product.PriceMin || 0),
        imageUrl: getProductImageUrl(product.ProductImagePath),
      }));

      const productsExternalResponse = await fetch(
        "https://www.grains.global/post_marketplace.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer AMTPL2025smTKWbApp",
          },
          body: JSON.stringify(productsData),
        }
      );

      if (!productsExternalResponse.ok) {
        throw new Error(
          `Failed to sync products to external website: ${productsExternalResponse.statusText}`
        );
      }

      setSyncResult({
        offers: {
          ...offersResponse.items,
          externalSynced: true,
        },
        products: {
          ...productsResponse.items,
          externalSynced: true,
        },
      });

      console.log("Sync completed successfully", {
        offers: offersResponse.items.count,
        products: productsResponse.items.count,
      });
    } catch (err) {
      console.error("Sync failed:", err);
      setError(err.message || "Failed to sync website data");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="buyer-dashboard">
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          General Settings
        </Typography>

        <Card sx={{ mt: 3, maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Website Data Sync
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sync all active offers and products to the website. This will
              fetch the latest data from the database.
            </Typography>

            <Button
              variant="contained"
              startIcon={
                syncing ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SyncIcon />
                )
              }
              onClick={handleSyncWebsite}
              disabled={syncing}
              sx={{
                mt: 2,
                backgroundColor: "#de6b14",
                color: "white",
                "&:hover": {
                  backgroundColor: "#c55f12",
                },
                "&:disabled": {
                  backgroundColor: "#de6b1480",
                  color: "white",
                },
              }}
            >
              {syncing ? "Syncing..." : "Sync Website"}
            </Button>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {syncResult && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mt: 2 }}
              >
                <Typography variant="body2">
                  <strong>Sync completed successfully!</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  • Offers synced to website: {syncResult.offers.count}
                </Typography>
                <Typography variant="body2">
                  • Products synced to website: {syncResult.products.count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "success.dark" }}
                >
                  ✓ Data successfully pushed to external website
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Synced at:{" "}
                  {new Date(syncResult.offers.syncedAt).toLocaleString()}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default GeneralSettings;
